import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CdkTableColumns, CdkTableTypeColumn } from '../../models/cdk-table.model';
import { faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
import { ItemAccount, TypePiece, TypePieceAsStrings } from '../../models/account/itemaccount.model';
import { EtatEcriture } from '../../models/etatEcriture.enum';
import { HttpServices } from 'src/app/services/http.service';
import { BillingFile } from 'src/app/models/account/billingFile.model';
import { ReferenceService } from 'src/app/services/reference.service';
import * as config from '../../config/config';

@Component({
  selector: 'fs-cdk-table',
  styleUrls: ['./cdk-table.component.sass'],
  templateUrl: './cdk-table.component.html'
})

export class CdkTableComponent implements OnInit {

  constructor(private httpServices: HttpServices, private referenceService: ReferenceService,
  ) { }
  @Input() tableId: string;
  @Input() rows: any[];
  @Input() displayedColumns: string[];
  @Input() columns: CdkTableColumns[];
  @Input() currentPage: number;
  @Input() askedPage: number;
  @Input() totalPages: any[];
  @Input() totalEntries: number;
  @Input() itemsPerPage: number;
  @Input() listSizeItemsPerPage: Array<number>;
  @Input() isAllRowsSelected: boolean;
  @Input() clickableRows?: boolean;

  @Output() changePage = new EventEmitter<number>();
  @Output() callbackPagination = new EventEmitter<any>();
  @Output() changeSizePage = new EventEmitter<number>();
  @Output() columnClicked = new EventEmitter();
  @Output() cellClicked = new EventEmitter();

  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faSort = faSort;

  CdkTableTypeColumn = CdkTableTypeColumn;
  TypePiece = TypePiece;
  EtatEcriture = EtatEcriture;

  private lastHandled = new WeakMap<EventTarget, number>();

  ngOnInit() { }

  onPageAsked(elts: any[]): void {
    this.askedPage = elts[0];
    this.changePage.emit(this.askedPage);
    elts[1].apply(this);
  }

  onCallbackPage(): void {
    this.callbackPagination.emit();
  }

  onChangeSizePage(sizePage: number): void {
    this.changeSizePage.emit(sizePage);
  }

  onClickColumn(column: CdkTableColumns): void {
    if (column.name === 'Panier' && this.tableId === 'balanceOutstanding') {
      this.columnClicked.emit(column);
      return;
    }

    if (column.sort === false) {
      return;
    }
    this.columns.forEach((col: CdkTableColumns) => {
      if (col.name !== column.name && col.sort !== false) {
        col.sort = '';
      }

      if (col.name === column.name) {
        switch (col.sort) {
          case '':
          case 'desc':
            col.sort = 'asc';
            break;
          case 'asc':
            col.sort = 'desc';
            break;
          case false:
            return;
          default:
            break;
        }

        this.columnClicked.emit(col);
      }
    });
  }

  getStatusDataTestid(columnName: string, isAvoir: boolean): string {
    if (isAvoir) {
      return 'asset';
    }
    switch (columnName) {
      case 'A régler':
        return 'bill-asset-statut-to-settle';
      case 'En retard':
        return 'bill-asset-statut-late';
      case 'Réglée':
        return 'bill-asset-statut-settled';
      case 'En traitement':
        return 'bill-asset-statut-processing';
      case 'Prélèvement en\u00A0cours':
        return 'bill-asset-statut-in-progress';
      case 'Prélèvement à\u00A0venir':
        return 'bill-asset-statut-direct-debit-upcoming';
      case 'En redressement':
        return 'bill-asset-statut-adjustement';
      case 'Etalement':
        return 'bill-asset-statut-staggering';
      default:
        return 'bill-asset-statut-undefined';
    }
  }

  onClickCell(row: any, column: CdkTableColumns, event: Event): void {
    if (!event.defaultPrevented) {
      this.cellClicked.emit({ row, column });
    }
  }

  private isUrlValid(event: Event, item: ItemAccount): void {
    // Normalize event: MouseEvent or KeyboardEvent
    let button = 0; // default to left activation
    let currentTarget: EventTarget | null = null;

    if ((event as MouseEvent).button !== undefined) {
      const me = event as MouseEvent;
      button = me.button; // 0 left, 1 middle, 2 right
      currentTarget = (me.currentTarget as EventTarget) || (me.target as EventTarget);
    } else if ((event as KeyboardEvent).key !== undefined) {
      // Keyboard activation (Enter/Space) — treat as left click
      const ke = event as KeyboardEvent;
      if (ke.key !== 'Enter' && ke.key !== ' ') {
        return;
      }
      button = 0;
      currentTarget = (ke.currentTarget as EventTarget) || (ke.target as EventTarget);
    } else {
      currentTarget = (event.currentTarget as EventTarget) || (event.target as EventTarget);
    }

    // Only handle left (0) and middle (1) activations here. Let others behave normally.
    if (button !== 0 && button !== 1) {
      return;
    }

    const target = currentTarget as EventTarget;

    // Guard against duplicate events (e.g. both mousedown and auxclick firing).
    const now = Date.now();
    const last = this.lastHandled.get(target);
    if (last && now - last < 500) {
      if ((event as any).preventDefault) { (event as any).preventDefault(); }
      if ((event as any).stopPropagation) { (event as any).stopPropagation(); }
      return;
    }
    this.lastHandled.set(target, now);

    if ((event as any).preventDefault) { (event as any).preventDefault(); }
    if ((event as any).stopPropagation) { (event as any).stopPropagation(); }

    const today = new Date();
    const isEarlyMonth = today.getDate() <= 4;
    const message = isEarlyMonth
      ? 'Un problème est survenu lors du chargement de votre facture. Il est possible que la facture soit encore en cours de génération, car nous sommes dans les premiers jours du mois. Nous vous invitons à réessayer demain.'
      : 'Un problème est survenu lors du chargement de votre facture. Veuillez contacter le service client ou réessayer plus tard.';

    // Open a blank window synchronously to avoid popup blockers. We'll navigate it once the URL is available.
    const popup = window.open('', '_blank');
    if (!popup) {
      // Popup blocked — inform the user and still proceed with the request so they can retry via the returned URL.
      window.alert('Impossible d\'ouvrir un nouvel onglet. Vérifiez les paramètres de votre navigateur ou autorisez les popups pour ce site.');
      return;
    }

    this.httpServices.getWithAuthHeader<BillingFile>
      (this.referenceService.staticReferences.Action[config.ApiKeys.CHECK_BILLINGFILE_EXISTS], { 'numPiece': item.DocumentNumber, 'typeDocument': TypePieceAsStrings[item.TypePiece] })
      .subscribe(
        response => {
          if (response.BillingFileUrl) {
            try {
              // Navigate the already-opened tab to the returned URL.
              popup.location.href = response.BillingFileUrl;
            } catch (e) {
              // Some browsers may restrict setting location on tabs opened with empty URL; fall back to opening directly.
              window.open(response.BillingFileUrl, '_blank');
              try { popup.close(); } catch (_) { /* ignore */ }
            }
          } else {
            try { popup.close(); } catch (_) { /* ignore */ }
            window.alert(message);
          }
        },
        error => {
          try { popup.close(); } catch (_) { /* ignore */ }
          window.alert(message);
        }
      );
  }
}
